import React, { ReactNode, useState } from 'react'
import { Drawer } from 'antd'
import OrderInformation from './Info'
import styled from '@emotion/styled'
import { PickupStatuses } from './Info/type'
import { isFullyRefunded } from './Info/utils'
import { SUBSCRIPTION_ORDER } from 'components/Orders/OrderQueries'
import { useSubscription } from '@apollo/client'
import { OrderInfo } from './Info/type'
import { OrderDrawerFooterNote } from './styles'

interface OrderDrawerProps {
  transactionId: string
  onClose: any
  visible: boolean
  ordersRefetch?: () => {}
  ordersDataRefetch?: () => {}
}

// Constants for drawer heights
const BASE_DRAWER_HEIGHT = 182
const PICKUP_DELIVERY_WITH_STATUS_HEIGHT = 254

const OrderDrawer = (props: OrderDrawerProps) => {
  const { transactionId, onClose, ordersRefetch, visible, ordersDataRefetch } =
    props
  const [order, setOrder] = useState<OrderInfo | null>(null)

  const { loading: isLoadingOrderData } = useSubscription(SUBSCRIPTION_ORDER, {
    variables: { transactionId },
    onSubscriptionData: ({ subscriptionData }) => {
      const newOrder = subscriptionData.data.orders[0]

      // If status or delivery status changed, update the key to force OrderSteps re-render
      if (
        !order ||
        order.status !== newOrder.status ||
        order.delivery?.delivery_status !== newOrder.delivery?.delivery_status
      ) {
        setOrder({ ...newOrder })
      }
    }
  })

  const { fulfillment_type } = order ?? {}

  const shouldShowUpdatePickupDeliveryStatus =
    order &&
    (fulfillment_type === 'pickup' ||
      order?.delivery?.service === 'merchant') &&
    !isFullyRefunded(order) &&
    order?.pickup_status !== PickupStatuses.collected &&
    !['rejected', 'pending'].includes(order.status || '')

  const drawerHeight = shouldShowUpdatePickupDeliveryStatus
    ? PICKUP_DELIVERY_WITH_STATUS_HEIGHT
    : BASE_DRAWER_HEIGHT

  const [footerEntries, setFooterEntries] = useState<
    ReactNode | ReactNode[] | null
  >(null)

  return (
    <StyledDrawer
      onClose={onClose}
      open={visible}
      className='order-drawer'
      height={drawerHeight}
      footer={
        <>
          {footerEntries}
          <OrderDrawerFooterNote>
            * Slerp fees will be deducted in your monthly statement
          </OrderDrawerFooterNote>
          <OrderDrawerFooterNote>
            * Delivery fee is based on the provided delivery location, in the
            case of an incorrect delivery location, the courier company may
            change the delivery estimate, contact the customer or subsequently
            cancel the delivery.
          </OrderDrawerFooterNote>
        </>
      }
    >
      {order && (
        <OrderInformation
          order={order}
          refetchCallback={() => {
            ordersRefetch && ordersRefetch()
            ordersDataRefetch && ordersDataRefetch()
          }}
          setFooterEntries={setFooterEntries}
          isLoading={isLoadingOrderData}
        />
      )}
    </StyledDrawer>
  )
}

const StyledDrawer = styled(Drawer)<{ height?: number }>`
  .ant-drawer-header.ant-drawer-header-close-only {
    position: relative;

    align-items: flex-start;
    min-height: ${({ height }) => height}px;
    background: transparent;
    border-bottom: none;

    .ant-drawer-header-title {
      display: flex;
      justify-content: flex-end;
    }

    .ant-drawer-header-title button.ant-drawer-close {
      background-color: #4a4a68;
      position: relative;
      z-index: 100;
    }
  }

  div.ant-drawer-header-no-title button.ant-drawer-close {
    margin: 24px 24px 0 auto;
    padding: 9px;

    span[role='img'].anticon.anticon-close {
      display: flex;
      align-items: center;
      justify-content: center;

      width: 18px;
      height: 18px;
    }

    span[role='img'].anticon.anticon-close svg {
      width: 12px;
      height: 12px;
    }
  }
`

export default OrderDrawer
